<script setup lang="ts">
import { computed, ref, useId } from 'vue'

// Drop zone + file picker. Emits the chosen files; files over maxBytes are reported in `rejected`
// (the caller shows the message) and never emitted.
const props = withDefaults(
  defineProps<{
    label: string
    hint?: string
    accept?: string
    multiple?: boolean
    maxBytes?: number
    disabled?: boolean
  }>(),
  { maxBytes: 8 * 1024 * 1024 },
)

const emit = defineEmits<{ files: [files: File[]]; rejected: [files: File[]] }>()

const inputId = `file-${useId()}`
const input = ref<HTMLInputElement | null>(null)
const over = ref(false)
const hintId = computed(() => (props.hint ? `${inputId}-hint` : undefined))

function take(list: FileList | null) {
  if (!list || props.disabled) return
  const files = Array.from(list).slice(0, props.multiple ? undefined : 1)
  const accepted = files.filter((file) => file.size <= props.maxBytes)
  const rejected = files.filter((file) => file.size > props.maxBytes)
  if (rejected.length) emit('rejected', rejected)
  if (accepted.length) emit('files', accepted)
  if (input.value) input.value.value = ''
}

function onDrop(event: DragEvent) {
  over.value = false
  take(event.dataTransfer?.files ?? null)
}
</script>

<template>
  <div
    class="ui-drop"
    :class="{ 'ui-drop--over': over, 'ui-drop--disabled': disabled }"
    @dragover.prevent="over = true"
    @dragleave="over = false"
    @drop.prevent="onDrop"
  >
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
    <label :for="inputId" class="ui-drop__label">{{ label }}</label>
    <span v-if="hint" :id="hintId" class="ui-drop__hint">{{ hint }}</span>
    <input
      :id="inputId"
      ref="input"
      type="file"
      class="ui-drop__input"
      :accept="accept"
      :multiple="multiple"
      :disabled="disabled"
      :aria-describedby="hintId"
      @change="take(($event.target as HTMLInputElement).files)"
    />
  </div>
</template>

<style scoped>
.ui-drop {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 16px;
  border: 1px dashed var(--muted-2);
  border-radius: var(--radius-list);
  color: var(--text-secondary);
  text-align: center;
  background: var(--surface-subtle);
}
.ui-drop--over {
  border-color: var(--accent);
  background: var(--surface-hover);
  color: var(--text-primary);
}
.ui-drop--disabled {
  opacity: 0.6;
}
.ui-drop__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-primary);
  text-decoration: underline;
  cursor: pointer;
}
.ui-drop__hint {
  font-size: var(--type-footnote-size);
}
.ui-drop__input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.ui-drop--disabled .ui-drop__input {
  cursor: default;
}
</style>
